import React, { useEffect, useState } from 'react';
import { CalendarClock, CalendarDays, CalendarRange, Infinity, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';

export default function FastFiltersBar({
  mode,
  currentFilter,
  onFilterChange,
  counts = {},
  dateRange,
  onCustomRangeChange,
}) {
  const [customOpen, setCustomOpen] = useState(false);

  useEffect(() => {
    if (mode !== 'date') return undefined;

    const handleKeyDown = (event) => {
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;

      if (event.ctrlKey && event.key === 't') {
        event.preventDefault();
        onFilterChange('today');
      }
      if (event.ctrlKey && event.key === 'm') {
        event.preventDefault();
        onFilterChange('tomorrow');
      }
      if (event.ctrlKey && event.key === 'n') {
        event.preventDefault();
        onFilterChange('next7');
      }
      if (event.ctrlKey && event.key === 'a') {
        event.preventDefault();
        onFilterChange('all');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, onFilterChange]);

  if (mode === 'date') {
    const presets = [
      { id: 'today', label: 'Heute', icon: CalendarClock },
      { id: 'tomorrow', label: 'Morgen', icon: CalendarDays },
      { id: 'next7', label: 'Naechste 7', icon: CalendarRange },
      { id: 'all', label: 'Alle', icon: Infinity },
    ];

    return (
      <div className="flex items-center gap-0.5 rounded-md bg-muted/20 p-0.5">
        {presets.map((preset) => {
          const Icon = preset.icon;
          return (
            <Button
              key={preset.id}
              variant={currentFilter === preset.id ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => onFilterChange(preset.id)}
              className={cn(
                'h-7 gap-1.5 px-2 text-xs transition-all',
                currentFilter === preset.id && 'bg-white font-medium text-primary shadow-sm ring-1 ring-black/5',
              )}
            >
              <Icon className="h-3.5 w-3.5 opacity-70" />
              <span>{preset.label}</span>
            </Button>
          );
        })}

        <Popover open={customOpen} onOpenChange={setCustomOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={currentFilter === 'custom' ? 'secondary' : 'ghost'}
              size="sm"
              className={cn(
                'h-7 gap-1.5 px-2 text-xs transition-all',
                currentFilter === 'custom' && 'bg-white font-medium text-primary shadow-sm ring-1 ring-black/5',
              )}
            >
              <CalendarDays className="h-3.5 w-3.5 opacity-70" />
              <span>Custom</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0">
            <Calendar
              mode="range"
              defaultMonth={dateRange?.start}
              selected={
                currentFilter === 'custom'
                  ? { from: dateRange?.start, to: dateRange?.end }
                  : undefined
              }
              onSelect={(range) => {
                if (!range) return;
                onCustomRangeChange?.(range);
                if (range.from && range.to) setCustomOpen(false);
              }}
              numberOfMonths={2}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {currentFilter !== 'today' ? (
          <Button
            variant="ghost"
            size="icon"
            className="ml-0.5 h-7 w-7 rounded-full text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            onClick={() => onFilterChange('clear')}
            title="Auf heute zuruecksetzen"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>
    );
  }

  if (mode === 'status') {
    const filters = [
      { id: 'all', label: 'Alle', count: () => counts.total || 0 },
      { id: 'upcoming', label: 'Aktiv', count: () => (counts.confirmed || 0) + (counts.seated || 0) + (counts.pending || 0) },
      { id: 'pending', label: 'Offen', count: () => counts.pending || 0 },
      { id: 'confirmed', label: 'Bestaetigt', count: () => counts.confirmed || 0 },
      { id: 'seated', label: 'Anwesend', count: () => counts.seated || 0 },
      { id: 'cancelled', label: 'Storniert', count: () => counts.cancelled || 0 },
    ];

    return (
      <div className="flex items-center gap-1 overflow-x-auto py-0.5">
        {filters.map((filter) => (
          <Button
            key={filter.id}
            variant={currentFilter === filter.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onFilterChange(filter.id)}
            className={cn(
              'h-6 whitespace-nowrap rounded-full border border-transparent px-2.5 text-[10px]',
              currentFilter !== filter.id && 'text-muted-foreground hover:bg-muted',
            )}
          >
            {filter.label}
            <span
              className={cn(
                'ml-1.5 min-w-[16px] rounded-full px-1 py-0 text-center text-[9px] transition-colors',
                currentFilter === filter.id
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {filter.count()}
            </span>
          </Button>
        ))}
      </div>
    );
  }

  return null;
}
